import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SkeletonRegion } from "@/components/ui/skeleton";
import { CheckboxCard, CheckboxCardSkeleton } from "@/components/CheckboxCard";
import { ListDetail } from "@/components/ListDetail";
import type {
  Filterdescriptor,
  Filterstand,
} from "@/components/ui/data-table-filter";
import { PersoonZoeker } from "@/features/koppelingen/PersoonZoeker";
import {
  scopeLabel,
  useBevoegdheden,
  useFuncties,
  useSetBevoegdheid,
  type Functie,
} from "./useStamgegevens";

/**
 * The act §4.3.1 gates on. Persoon-first, because a beheerder arrives with a name. `toonIedereen`
 * is on: nobody is being identified here, and behind an empty search box a database full of
 * bevoegdheden reads as an empty one (unlike the koppeling screen, per ADR-0018).
 */
export function Bevoegdheden({
  term,
  onZoek,
  persoonId,
  onKies,
  pagina,
  onPaginaChange,
  functieIds,
  onFuncties,
}: {
  term: string;
  onZoek: (term: string) => void;
  persoonId: string | null;
  onKies: (persoonId: string | null) => void;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
  functieIds: string[];
  onFuncties: (functieIds: string[]) => void;
}) {
  const { t } = useTranslation("stamgegevens");
  const { data: functies } = useFuncties();

  /**
   * The reverse lookup #176 asked for: a wedstrijdleider short of a pompbediener needs the
   * people, not one persoon at a time. Several at once means **anyone bevoegd for one of
   * them**, because that is the question staffing a baan actually asks.
   *
   * No `past`: the server narrows this list, so the facet shows no counts - it cannot count what
   * it never fetched.
   */
  const descriptors = useMemo<Filterdescriptor<unknown>[]>(
    () => [
      {
        id: "functie",
        label: t("bevoegdheden.filterFunctie"),
        kind: "meerkeuze",
        opties: (functies ?? []).map((functie) => ({
          waarde: functie.id,
          label: functie.name,
        })),
      },
    ],
    [functies, t],
  );

  const stand: Filterstand =
    functieIds.length > 0
      ? { functie: { soort: "meerkeuze", waarden: functieIds } }
      : {};

  return (
    <ListDetail
      selected={Boolean(persoonId)}
      backLabel={t("bevoegdheden.terug")}
      onBack={() => onKies(null)}
      list={
        <Card>
          <CardContent className="pt-6">
            <PersoonZoeker
              term={term}
              onZoek={onZoek}
              gekozen={persoonId}
              onKies={onKies}
              toonIedereen
              alleenOndersteuners
              exportable
              pagina={pagina}
              onPaginaChange={onPaginaChange}
              filters={{
                descriptors,
                stand,
                onStand: (nieuw) => {
                  const waarde = nieuw.functie;
                  onFuncties(
                    waarde?.soort === "meerkeuze" ? waarde.waarden : [],
                  );
                },
              }}
            />
          </CardContent>
        </Card>
      }
      detail={
        persoonId ? (
          <PersoonBevoegdheden key={persoonId} persoonId={persoonId} />
        ) : (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                {t("bevoegdheden.kies")}
              </p>
            </CardContent>
          </Card>
        )
      }
    />
  );
}

function PersoonBevoegdheden({ persoonId }: { persoonId: string }) {
  const { t } = useTranslation("stamgegevens");
  const { data: functies, isPending: functiesPending } = useFuncties();
  const { data: bevoegdheden, isPending } = useBevoegdheden(persoonId);
  const zet = useSetBevoegdheid(persoonId);

  // The card, its title and its explanation say the same thing either way, so only the boxes
  // wait on the server - and only the boxes are drawn as greys.
  const laden = isPending || functiesPending || !functies || !bevoegdheden;

  const bevoegdVoor = new Set(bevoegdheden?.map((b) => b.functieId));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("bevoegdheden.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("bevoegdheden.uitleg")}
        </p>

        {laden ? (
          <BevoegdheidSkeleton />
        ) : (
          <FunctieBevoegdheidLijst
            functies={functies}
            bevoegdVoor={bevoegdVoor}
            zetPending={zet.isPending}
            onZet={(payload) => zet.mutate(payload)}
          />
        )}

        <BevoegdheidMeldingen
          geenFuncties={functies?.length === 0}
          fout={zet.error}
        />
      </CardContent>
    </Card>
  );
}

function BevoegdheidSkeleton() {
  return (
    <SkeletonRegion>
      <ul className="grid gap-2 sm:grid-cols-2">
        {Array.from({ length: 6 }, (_, rij) => (
          <li key={rij}>
            <CheckboxCardSkeleton />
          </li>
        ))}
      </ul>
    </SkeletonRegion>
  );
}

function BevoegdheidMeldingen({
  geenFuncties,
  fout,
}: {
  geenFuncties: boolean;
  fout: Error | null;
}) {
  const { t } = useTranslation("stamgegevens");

  return (
    <>
      {geenFuncties && (
        <p className="text-sm">{t("bevoegdheden.geenFuncties")}</p>
      )}
      {fout && <p className="text-sm text-destructive">{fout.message}</p>}
    </>
  );
}

function FunctieBevoegdheidLijst({
  functies,
  bevoegdVoor,
  zetPending,
  onZet,
}: {
  functies: Functie[];
  bevoegdVoor: Set<string>;
  zetPending: boolean;
  onZet: (payload: { functieId: string; bevoegd: boolean }) => void;
}) {
  return (
    <ul className="grid gap-2 sm:grid-cols-2">
      {functies.map((functie) => {
        const bevoegd = bevoegdVoor.has(functie.id);
        return (
          <li key={functie.id}>
            <CheckboxCard
              id={`bevoegd-${functie.id}`}
              title={functie.name}
              description={scopeLabel(functie.scope)}
              checked={bevoegd}
              disabled={zetPending}
              onCheckedChange={() =>
                onZet({ functieId: functie.id, bevoegd: !bevoegd })
              }
            />
          </li>
        );
      })}
    </ul>
  );
}
